import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';

interface Business {
  id: string;
  name: string;
  address: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  logoUrl?: string;
  status?: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const VerificationSection = styled.div`
  width: 100%;
`;

const MessageContainer = styled.div`
  padding: 0;
  margin-bottom: 24px;
  width: 100%;
  text-align: left;
`;

const VerificationMessage = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 32px;
`;

const BusinessName = styled.span`
  /* Remove styles - default text appearance */
`;

const ContactLink = styled.span`
  color: #000;
  text-decoration: underline;
  cursor: pointer;
  font-size: 18px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoadingPlaceholder = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const BusinessVerificationPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setCurrentUser(userData);

        if (!userData) {
          navigate('/login');
          return;
        }

        if (businessId) {
          const businessDoc = await getDoc(doc(db, 'businesses', businessId));
          
          if (!businessDoc.exists()) {
            setError('Business not found');
            return;
          }

          const businessData = businessDoc.data();
          
          // Check if user has access to this business
          if (businessData.managerId !== userData.id) {
            setError('You do not have access to this business');
            return;
          }

          setBusiness({
            id: businessDoc.id,
            ...businessData
          } as Business);
        }
      } catch (error) {
        console.error('Error fetching business data:', error);
        setError('Failed to load business data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId, navigate]);

  const handleContactUs = () => {
    navigate('/contact-us');
  };

  const getLocationText = () => {
    if (!business) return '';
    
    // Try to use individual address components first
    if (business.street && business.city) {
      return `${business.street} in ${business.city}`;
    }
    
    // Fall back to full address
    if (business.address) {
      const addressParts = business.address.split(',');
      if (addressParts.length >= 2) {
        const street = addressParts[0].trim();
        const city = addressParts[1].trim();
        return `${street} in ${city}`;
      }
    }
    
    return business.address || 'your location';
  };

  if (loading) {
    return (
      <Container>
        <LoadingPlaceholder>Loading business information...</LoadingPlaceholder>
      </Container>
    );
  }

  if (!currentUser || !business) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - {business.name} Verification</title>
        <meta name="description" content="Business verification status" />
      </Helmet>
      
      <Container>
        <ContentWrapper>
          <TopBar user={currentUser} />
          
          <PageTitle>{business.name}</PageTitle>
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={() => navigate('/dashboard')}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={() => navigate('/businesses')}>Businesses</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>{business.name}</span>
          </Breadcrumbs>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <VerificationSection>
            <MessageContainer>
              <VerificationMessage>
                Thank you for adding <BusinessName>{business.name}</BusinessName> located at {getLocationText()}. 
                Your request is currently pending until we can verify you are the owner of this business. 
                One of our team members will contact you. If you don't hear from us or if you have any questions, 
                don't hesitate to <ContactLink onClick={handleContactUs}>contact us</ContactLink>.
              </VerificationMessage>
              
              <Button
                text="Contact Us"
                onClick={handleContactUs}
                bgColor="#000"
                textColor="#fff"
                width="auto"
                minWidth="150px"
              />
            </MessageContainer>
          </VerificationSection>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default BusinessVerificationPage;
